import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { crawlApi } from '../services/apiService'
import toast from 'react-hot-toast'

const GlobalCrawlStatus = () => {
  const { t } = useTranslation()
  const [currentCrawl, setCurrentCrawl] = useState(null)
  const [isActive, setIsActive] = useState(false)
  const [loading, setLoading] = useState(false)

  const checkCrawlStatus = async () => {
    try {
      setLoading(true)
      const data = await crawlApi.getCurrentCrawlStatus()
      setCurrentCrawl(data)
      setIsActive(true)
    } catch (error) {
      if (error.response?.status === 404) {
        setCurrentCrawl(null)
        setIsActive(false)
      } else {
        console.error('Error checking crawl status:', error)
      }
    } finally {
      setLoading(false)
    }
  }

  const checkIfActive = async () => {
    try {
      const data = await crawlApi.isCrawlActive()
      setIsActive(data)
      if (!data) {
        setCurrentCrawl(null)
      }
    } catch (error) {
      console.error('Error checking if crawl is active:', error)
    }
  }

  useEffect(() => {
    checkCrawlStatus()
  }, [])

  useEffect(() => {
    if (isActive) {
      const interval = setInterval(checkCrawlStatus, 5000)
      return () => clearInterval(interval)
    } else {
      const interval = setInterval(checkIfActive, 5000)
      return () => clearInterval(interval)
    }
  }, [isActive])

  if (!isActive && !currentCrawl) {
    return null
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'text-yellow-600 bg-yellow-100'
      case 'in_progress':
        return 'text-primary-800 bg-primary-100'
      case 'completed':
        return 'text-green-600 bg-green-100'
      case 'failed':
        return 'text-red-600 bg-red-100'
      default:
        return 'text-gray-600 bg-gray-100'
    }
  }

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return t('globalCrawlStatus.waitingToStart')
      case 'in_progress':
        return t('globalCrawlStatus.crawlingInProgress')
      case 'completed':
        return t('globalCrawlStatus.crawlCompleted')
      case 'failed':
        return t('globalCrawlStatus.crawlFailed')
      default:
        return status
    }
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-light-200/20 p-6 border-l-4 border-l-primary-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="bg-primary-500/10 p-2 rounded-lg">
            <i className="fa-solid fa-spider text-primary-500 text-xl"></i>
          </div>
          <div>
            <h3 className="text-lg font-bold text-dark-300">{t('globalCrawlStatus.title')}</h3>
            <p className="text-sm text-dark-100">
              {currentCrawl ? t('globalCrawlStatus.crawling', { url: currentCrawl.url }) : t('globalCrawlStatus.noActiveCrawl')}
            </p>
          </div>
        </div>
        
        {currentCrawl && (
          <div className="flex items-center space-x-4">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(currentCrawl.status)}`}>
              {getStatusText(currentCrawl.status)}
            </div>
            
            {currentCrawl.status === 'in_progress' && (
              <div className="flex items-center space-x-2">
                <div className="animate-spin h-4 w-4">
                  <i className="fa-solid fa-spinner text-primary-500"></i>
                </div>
                <span className="text-sm text-dark-100">
                  {currentCrawl.pages_crawled} / {currentCrawl.total_pages} {t('globalCrawlStatus.pages')}
                </span>
              </div>
            )}
          </div>
        )}
      </div>

      {currentCrawl && currentCrawl.status === 'in_progress' && (
        <div className="mt-4">
          <div className="flex justify-between text-sm text-dark-100 mb-1">
            <span>{t('globalCrawlStatus.progress')}</span>
            <span>{Math.round((currentCrawl.pages_crawled / currentCrawl.total_pages) * 100)}%</span>
          </div>
          <div className="w-full bg-light-100 rounded-full h-2">
            <div 
              className="gradient-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentCrawl.pages_crawled / currentCrawl.total_pages) * 100}%` }}
            ></div>
          </div>
        </div>
      )}

      {currentCrawl && currentCrawl.error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            <strong>{t('globalCrawlStatus.error')}:</strong> {currentCrawl.error}
          </p>
        </div>
      )}
    </div>
  )
}

export default GlobalCrawlStatus
